import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';

// COMPONENTS
import Button from '../../components/Button';
import FileUpload from '../../components/FileUpload';
import SingleSelect from '../../components/SingleSelect';
import FormGroupWithError from '../../components/FormGroupWithError';

// TYPES
import { OrganisationRegistrationCommand, PaymentPlansPaymentPlan, DomainOrganisationStatus } from '../../api/models';

interface RegisterOrganisationFormTypes {
	organisationDetails: OrganisationRegistrationCommand;
	setOrganisationDetails: React.Dispatch<React.SetStateAction<OrganisationRegistrationCommand>>;
	planName: string;
	doRegister: (values: OrganisationRegistrationCommand) => Promise<void>;
}

interface OrganisationDetailFullFormTypes extends OrganisationRegistrationCommand {
	confirmAuthority?: boolean;
	confirmAgreement?: boolean;
}

export const RegisterOrganisationForm: React.FC<RegisterOrganisationFormTypes> = ({
	organisationDetails,
	setOrganisationDetails,
	planName,
	doRegister
}) => {
	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Error and Loading state
	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	// Set up Data state
	const [planData, setPlanData] = React.useState<PaymentPlansPaymentPlan[]>([]);

	const [planItemName, setPlanItemName] = React.useState<string>(planName);

	const formRef = React.createRef<HTMLFormElement>();
	const formikInstance = React.useRef<any>();

	const statesList = useAppSelector((state) => state.lists.states);
	const plansList = useAppSelector((state) => state.lists.paymentPlans);

	const getPlans = async () => {
		setIsLoading(true);

		const response = await apibridge.getPlanTypes();

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result?.paymentPlans) {
			setPlanData(response.result.paymentPlans);
		}

		setIsLoading(false);
	};

	React.useEffect(() => {
		getPlans();
	}, []);

	const getPlanName = (val: string) => {
		const name = planData.filter((plan) => plan.id === val)[0].name;

		if (name) {
			setPlanItemName(name);
		}
	};

	const maxPlan = () => {
		const plan = planData.filter((plan) => plan.name === planItemName)[0];

		const max = (plan && plan.maximumChildren) || undefined;

		if (max) {
			return max;
		} else {
			return 99999999;
		}
	};

	const cacheAsyncTest = (asyncValidationFunction: any) => {
		let asyncInProgress: boolean = false;
		let previousValue: any;
		let responseData: any;

		return async (value: any, organisationId: any, throwErrorMessage: (msg: string) => any) => {
			if (asyncInProgress) {
				return throwErrorMessage('Please wait');
			} else if (value !== previousValue) {
				asyncInProgress = true;
				const response = await asyncValidationFunction(value, organisationId);
				previousValue = value;
				responseData = response;
				asyncInProgress = false;
			}

			return responseData;
		};
	};

	const stripSpaces = (str: string) => {
		return str.replace(/\s/g, '');
	};

	const formatAbnSpaces = (str?: string) => {
		if (!str) {
			return str;
		}

		const stripStr = stripSpaces(str);

		// prettier-ignore
		return [
			stripStr.substring(0, 2),
			stripStr.substring(2, 5),
			stripStr.substring(5, 8),
			stripStr.substring(8)
		].join(' ').trim();
	};

	const isAbnUnique = async (str?: string, organisationId?: string) => {
		// "Valid"|"Invalid"|"InUse"
		let status = { resultType: 'Invalid' };

		if (!str || str.length !== 11) {
			return status;
		}

		const response = await apibridge.checkAbnInUse(str, organisationId || '');

		if (response.result) {
			status = response.result;
		}

		return status;
	};

	const cachedIsAbnUnique = React.useRef(cacheAsyncTest(isAbnUnique));

	const saveForm = (values: OrganisationDetailFullFormTypes) => {
		const newData: any = {
			organisationAddress: {
				address1: values.organisationAddress?.address1,
				address2: values.organisationAddress?.address2,
				city: values.organisationAddress?.city,
				postcode: values.organisationAddress?.postcode,
				state: values.organisationAddress?.state
			},
			sameAddresses: values.sameAddresses,
			mailingAddress: {
				address1: values.mailingAddress?.address1,
				address2: values.mailingAddress?.address2,
				city: values.mailingAddress?.city,
				postcode: values.mailingAddress?.postcode,
				state: values.mailingAddress?.state
			},
			...values
		};

		setOrganisationDetails(newData);

		doRegister(newData);
	};

	const initialValues: OrganisationDetailFullFormTypes = {
		id: undefined,
		imageFileName: undefined,
		name: '',
		paymentPlanId: undefined,
		australianBusinessNumber: '',
		numberOfChildren: 0,
		phoneNumber: '',
		email: '',
		confirmAuthority: false,
		confirmAgreement: false,
		organisationAddress: {
			address1: '',
			address2: '',
			city: '',
			postcode: '',
			state: ''
		},
		mailingAddress: {
			address1: '',
			address2: '',
			city: '',
			postcode: '',
			state: ''
		},
		sameAddresses: false,

		...organisationDetails
	};

	// FORM VALIDATION FOR AUSTRALIAN PHONE NUMBERS
	const phoneRegExp =
		/^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/;

	const validationSchema = Yup.object({
		name: Yup.string().required('Required'),
		paymentPlanId: Yup.string().required('Required'),
		australianBusinessNumber: Yup.string()
			.required('Required')
			.matches(/^[\d]+$/, 'Only numbers are allowed')
			.length(11, 'ABN must be 11 numbers')
			.test(
				'abn-check',
				'This ABN matches with another organisation registered with ChildAware',
				function (value, organisationId) {
					formikInstance.current.setFieldError(this.path, 'Please wait');

					const throwErrorMessage = (msg: any) => this.createError({ message: msg, path: this.path });

					return new Promise(async (resolve) => {
						// "Valid"|"Invalid"|"InUse"

						const response = await cachedIsAbnUnique.current(value, organisationId.parent.id, throwErrorMessage);

						if (response && response.resultType === 'Valid') {
							// no error message
							resolve(true);
						} else if (response && response.resultType === 'InUse') {
							// error message defined on rule (html markup)
							// resolve(false);
							resolve(
								throwErrorMessage(() => (
									<>
										{response.existingOrganisationStatus === DomainOrganisationStatus.Active ? (
											<>
												This ABN matches with another organisation registered with ChildAware. You can{' '}
												<Link to={`/find-organisation?id=${response.existingOrganisationIdentifier}&type=staff`}>
													join this organisation
												</Link>
												, or please <a href={`mailto:${response.contactEmail}`}>contact&nbsp;ChildAware</a> for
												assistance.
											</>
										) : (
											<>
												This organisation matches another organisation registered with ChildAware. Please{' '}
												<a href={`mailto:${response.contactEmail}`}>contact&nbsp;ChildAware</a> for assistance.
											</>
										)}
									</>
								))
							);
						} else if (response && response.resultType === 'Invalid') {
							// custom error message
							resolve(throwErrorMessage('Please enter a valid ABN'));
						} else {
							// passthrough error message from async call ('Please wait')
							resolve(response);
						}
					});
				}
			),
		numberOfChildren: Yup.number()
			.required('Required')
			.min(50, 'A minimum of 50 children is required')
			.max(maxPlan(), `Maximum of ${maxPlan()}`),
		email: Yup.string().required('Required'),
		phoneNumber: Yup.string().matches(phoneRegExp, 'Phone number is not valid'),
		organisationAddress: Yup.object({
			address1: Yup.string().required('Required'),
			city: Yup.string().required('Required'),
			state: Yup.string().required('Required'),
			postcode: Yup.string()
				.required('Required')
				.matches(/^[\d]+$/, 'Only numbers are allowed')
				.length(4, 'Postcode must be 4 numbers')
		}),
		mailingAddress: Yup.object({
			address1: Yup.string().when('sameAddresses', {
				is: false,
				then: Yup.string().required('Required')
			}),
			city: Yup.string().when('sameAddresses', {
				is: false,
				then: Yup.string().required('Required')
			}),
			postcode: Yup.string().when('sameAddresses', {
				is: false,
				then: Yup.string()
					.required('Required')
					.matches(/^[\d]+$/, 'Only numbers are allowed')
					.length(4, 'Postcode must be 4 numbers')
			}),
			state: Yup.string().when('sameAddresses', {
				is: false,
				then: Yup.string().required('Required')
			})
		}),
		confirmAuthority: Yup.bool().oneOf([true], 'Required').required('Required'),
		confirmAgreement: Yup.bool().oneOf([true], 'Required').required('Required')
	});

	return (
		<div className="register-organisation-form">
			<div className="section-header">
				<h1 className="h1">Register an organisation</h1>
				<p className="sub-grey-text">Let's collect some simple details to begin</p>
			</div>

			<Formik
				enableReinitialize={true}
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={saveForm}
			>
				{(formik) => {
					formikInstance.current = formik;
					return (
						<Form translate="yes" ref={formRef} className="account-submit">
							<div className="header">
								<h2 className="h4">Organisation details</h2>
							</div>
							<div className="content">
								<div className="form-group">
									<label htmlFor="imageFileName">
										Upload your organisation’s logo <span className="note">(optional)</span>
									</label>

									<FileUpload
										name="imageFileName"
										onChange={(val: string) => formik.setFieldValue('imageFileName', val)}
										multiple={false}
										maxSizeMb={5}
										imagesOnly={true}
									></FileUpload>
								</div>

								<FormGroupWithError name="name">
									<label htmlFor="name">Organisation name</label>
									<Field name="name" id="name" type="text" className="form-control" />
								</FormGroupWithError>

								<FormGroupWithError name="paymentPlanId">
									<label htmlFor="paymentPlanId">Organisation type</label>
									<SingleSelect
										options={plansList}
										name="paymentPlanId"
										value={formik.values.paymentPlanId}
										onChange={(val: string) => {
											formik.setFieldValue('paymentPlanId', val), getPlanName(val);
										}}
									/>
								</FormGroupWithError>

								<FormGroupWithError name="australianBusinessNumber">
									<label htmlFor="australianBusinessNumber">Australian Business Number (ABN)</label>
									<Field
										name="australianBusinessNumber"
										id="australianBusinessNumber"
										type="text"
										className="form-control"
										value={formatAbnSpaces(formik.values.australianBusinessNumber)}
										onChange={(e: React.ChangeEvent<any>) =>
											formik.setFieldValue('australianBusinessNumber', stripSpaces(e.target.value))
										}
									/>
								</FormGroupWithError>

								<FormGroupWithError name="numberOfChildren">
									<label htmlFor="numberOfChildren">Number of children at your organisation or licenced places</label>
									<Field name="numberOfChildren" id="numberOfChildren" type="number" className="form-control" />
								</FormGroupWithError>

								<FormGroupWithError name="phoneNumber">
									<label htmlFor="phoneNumber">Phone number</label>
									<Field name="phoneNumber" id="phoneNumber" type="text" className="form-control" />
									<p className="text-subtitle">For a landline, please include an area code e.g. 03 XXX</p>
								</FormGroupWithError>

								<FormGroupWithError name="email">
									<label htmlFor="email">Organisation email</label>
									<Field name="email" id="email" type="text" className="form-control" />
								</FormGroupWithError>

								{/* ORGANISATION ADDRESS */}
								<div className="form-group">
									<h2 className="h4 mtop-50">Organisation address</h2>
								</div>

								<FormGroupWithError name="organisationAddress.address1">
									<label htmlFor="organisationAddress.address1">Address line 1</label>
									<Field
										name="organisationAddress.address1"
										id="organisationAddress.address1"
										type="text"
										className="form-control"
									/>
								</FormGroupWithError>

								<FormGroupWithError name="organisationAddress.address2">
									<label htmlFor="organisationAddress.address2">Address line 2</label>
									<Field
										name="organisationAddress.address2"
										id="organisationAddress.address2"
										type="text"
										className="form-control"
									/>
								</FormGroupWithError>

								<FormGroupWithError name="organisationAddress.city">
									<label htmlFor="organisationAddress.city">City</label>
									<Field
										name="organisationAddress.city"
										id="organisationAddress.city"
										type="text"
										className="form-control"
									/>
								</FormGroupWithError>

								<div className="row">
									<div className="col-8 col-lg-8">
										<FormGroupWithError name="organisationAddress.state">
											<label htmlFor="organisationAddress.state">State/Territory</label>
											<SingleSelect
												options={statesList}
												name="organisationAddress.state"
												value={formik.values.organisationAddress?.state}
												onChange={(val: string) => formik.setFieldValue('organisationAddress.state', val)}
											/>
										</FormGroupWithError>
									</div>
									<div className="col-4 col-lg-4">
										<FormGroupWithError name="organisationAddress.postcode">
											<label htmlFor="organisationAddress.postcode">Postcode</label>
											<Field
												id="organisationAddress.postcode"
												name="organisationAddress.postcode"
												type="number"
												className="form-control"
											/>
										</FormGroupWithError>
									</div>
								</div>

								{/* MAILING ADDRESS */}
								<FormGroupWithError name="sameAddresses" className="acknowledge-container">
									<label className="form-control-checkbox">
										<Field name="sameAddresses" type="checkbox" />

										<span>Mailing address is the same as above</span>
									</label>
								</FormGroupWithError>

								{formik.values.sameAddresses === false && (
									<>
										<div className="form-group">
											<h2 className="h4 mtop-50">Mailing address</h2>
										</div>

										<FormGroupWithError name="mailingAddress.address1">
											<label htmlFor="mailingAddress.address1">Address line 1</label>
											<Field
												name="mailingAddress.address1"
												id="mailingAddress.address1"
												type="text"
												className="form-control"
											/>
										</FormGroupWithError>

										<FormGroupWithError name="mailingAddress.address2">
											<label htmlFor="mailingAddress.address2">Address line 2</label>
											<Field
												name="mailingAddress.address2"
												id="mailingAddress.address2"
												type="text"
												className="form-control"
											/>
										</FormGroupWithError>

										<FormGroupWithError name="mailingAddress.city">
											<label htmlFor="mailingAddress.city">City</label>
											<Field name="mailingAddress.city" id="mailingAddress.city" type="text" className="form-control" />
										</FormGroupWithError>

										<div className="row">
											<div className="col-8 col-lg-8">
												<FormGroupWithError name="mailingAddress.state">
													<label htmlFor="mailingAddress.state">State/Territory</label>
													<SingleSelect
														options={statesList}
														name="mailingAddress.state"
														value={formik.values.mailingAddress?.state}
														onChange={(val: string) => formik.setFieldValue('mailingAddress.state', val)}
													/>
												</FormGroupWithError>
											</div>

											<div className="col-4 col-lg-4">
												<FormGroupWithError name="mailingAddress.postcode">
													<label htmlFor="mailingAddress.postcode">Postcode</label>
													<Field
														id="mailingAddress.postcode"
														name="mailingAddress.postcode"
														type="number"
														className="form-control"
													/>
												</FormGroupWithError>
											</div>
										</div>
									</>
								)}

								<FormGroupWithError name="confirmAuthority" className="acknowledge-container">
									<label className="form-control-checkbox">
										<Field name="confirmAuthority" type="checkbox" />

										<span>
											I have authority to act on behalf of this organisation and to enter into a binding agreement with
											ChildAware.
										</span>
									</label>
								</FormGroupWithError>

								<FormGroupWithError name="confirmAgreement" className="acknowledge-container">
									<label className="form-control-checkbox">
										<Field name="confirmAgreement" type="checkbox" />

										<span>
											I agree to the ChildAware Customer{' '}
											<a href={`${websiteUrl}/terms-conditions-customer`} target="_blank">
												Terms and Conditions
											</a>{' '}
											and{' '}
											<a href={`${websiteUrl}/privacy-policy`} target="_blank">
												Privacy Policy
											</a>{' '}
											on behalf of this organisation.
										</span>
									</label>
								</FormGroupWithError>
							</div>

							<div className="actions">
								<Button type="submit" className="btn-primary full-width" disabled={isLoading}>
									Continue
								</Button>
							</div>
						</Form>
					);
				}}
			</Formik>
		</div>
	);
};

export default RegisterOrganisationForm;
